import { DndContext } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import AddIcon from '@mui/icons-material/Add';
import InventoryIcon from '@mui/icons-material/Inventory';
import { Button, Stack, Typography } from '@mui/material';
import React, { useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import useConfirm from '../../hooks/useConfirm';
import { newSection } from '../../validationSchemas/ReportTemplateSchema';
import SectionForm from './SectionForm';
import SectionItemSortable from './SectionItemSortable';
import { useReportingHubContext } from './useReportingHubContext';

function Configuration() {
  const setValueOptions = {
    shouldDirty: true,
    shouldValidate: true,
  };
  const { activeSection, activeSheet, setActiveSection } = useReportingHubContext();
  const [isEdit, setIsEdit] = useState(false);
  const [selectedSheet, setSelectedSheet] = useState(activeSheet);
  const form = useFormContext();
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(null);
  const { windowDotConfirm } = useConfirm(
    `Are you sure you want to delete this section from ${activeSheet?.sheetName}?`
  );
  const reportSections = activeSheet?.reportSections?.map((sectionItem, index) => ({ ...sectionItem, id: index + 1 }));
  const latestSectionId = useRef((reportSections && reportSections[reportSections.length - 1]?.id) || 0);

  const getNextSectionId = () => {
    latestSectionId.current += 1;
    return latestSectionId.current;
  };

  const editSection = (section, index) => {
    setIsEdit(true);
    setSelectedSheet({
      ...activeSheet,
    });
    setActiveSection(section);
    setSelectedSectionIndex(index);
  };

  const addNewSection = () => {
    setIsEdit(false);
    setSelectedSheet({
      ...activeSheet,
    });
    setActiveSection({
      ...newSection,
      id: getNextSectionId(),
    });
  };

  const saveSection = (section) => {
    const { getValues, setValue } = form;

    const sheetObjects = getValues('sheetObjects');
    const selectedSheetIndex = sheetObjects.map((sheet) => sheet.id).indexOf(selectedSheet.id);

    let newReportSections = sheetObjects[selectedSheetIndex].reportSections;
    if (isEdit) {
      newReportSections[selectedSectionIndex] = section;
    } else {
      if (!newReportSections) {
        newReportSections = [];
      }
      newReportSections.push(section);
    }

    setValue(`sheetObjects.${selectedSheetIndex}.reportSections`, newReportSections, setValueOptions);
    setActiveSection(null);
  };

  const removeSection = async (sectionName) => {
    if (await windowDotConfirm()) {
      const { getValues, setValue } = form;

      const sheetObjects = getValues('sheetObjects');
      const selectedSheetIndex = sheetObjects.map((sheet) => sheet.id).indexOf(activeSheet.id);

      const newReportSections = sheetObjects[selectedSheetIndex].reportSections.filter(
        (sectionItem) => sectionItem.sectionName !== sectionName
      );
      setValue(`sheetObjects.${selectedSheetIndex}.reportSections`, newReportSections, setValueOptions);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      const { getValues, setValue } = form;

      const sheetObjects = getValues('sheetObjects');
      const selectedSheetIndex = sheetObjects.map((sheet) => sheet.id).indexOf(activeSheet.id);
      const reportSectionsIds = reportSections.map((section) => section.id);
      const oldIndex = reportSectionsIds.indexOf(active.id);
      const newIndex = reportSectionsIds.indexOf(over.id);
      const newReportSections = arrayMove(reportSections, oldIndex, newIndex);

      setValue(`sheetObjects.${selectedSheetIndex}.reportSections`, newReportSections, setValueOptions);
    }
  };

  return (
    <>
      {!activeSection && (
        <Stack justifyContent="space-between" height="100%">
          {(!reportSections || reportSections?.length === 0) && (
            <Stack p={3} mt={4} alignItems="center" textAlign="center">
              <InventoryIcon fontSize="large" color="disabled" />
              <Typography variant="h6">No sections have been added.</Typography>
              <Typography>Click &quot;Add Section&quot; to add one to &quot;{activeSheet.sheetName}&quot;.</Typography>
            </Stack>
          )}
          {reportSections?.length > 0 && (
            <DndContext onDragEnd={handleDragEnd}>
              <Stack height="100%" sx={{ overflowY: 'scroll' }} pb={10}>
                <SortableContext items={reportSections} strategy={verticalListSortingStrategy}>
                  {reportSections?.map((item, index) => (
                    <SectionItemSortable
                      key={item.sectionName}
                      section={item}
                      index={index}
                      onEdit={editSection}
                      onRemove={removeSection}
                    />
                  ))}
                </SortableContext>
              </Stack>
            </DndContext>
          )}
          {(!reportSections || reportSections?.length < 6) && (
            <Stack p={1} className="reporting-hub-sticky-CTA">
              <Button variant="text" startIcon={<AddIcon />} onClick={addNewSection}>
                Add Section
              </Button>
            </Stack>
          )}
        </Stack>
      )}
      {activeSection && selectedSheet && <SectionForm sheet={selectedSheet} isEdit={isEdit} onChange={saveSection} />}
    </>
  );
}

export default Configuration;
